import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { rulerStore } from '../stores/ruler-store';
import './ruler-ui.scss';

export const RulerUI: React.FC = observer(() => {
  const [activeTab, setActiveTab] = useState<'ruler' | 'calibration' | 'measurements'>('ruler');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [endPoint, setEndPoint] = useState<{ x: number; y: number } | null>(null);
  const [measurementName, setMeasurementName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (activeTab !== 'ruler') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setStartPoint({ x, y });
    setEndPoint({ x, y });
    setIsDrawing(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setEndPoint({ x, y });
  };

  const handleMouseUp = () => {
    if (!isDrawing || !startPoint || !endPoint) return;

    setIsDrawing(false);
    
    const distance = Math.sqrt(
      Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2)
    );

    if (distance > 5) { // Only save if line is meaningful
      setShowNameInput(true);
    } else {
      // Clear if too short
      setStartPoint(null);
      setEndPoint(null);
    }
  };

  const saveMeasurement = () => {
    if (!startPoint || !endPoint) return;

    const distance = Math.sqrt(
      Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2)
    );

    const name = measurementName.trim() || `Measurement ${rulerStore.allMeasurements.length + 1}`;
    rulerStore.addMeasurement(name, distance);

    // Reset
    setMeasurementName('');
    setShowNameInput(false);
    setStartPoint(null);
    setEndPoint(null);
  };

  const cancelMeasurement = () => {
    setMeasurementName('');
    setShowNameInput(false);
    setStartPoint(null);
    setEndPoint(null);
  };

  // Canvas drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    
    const gridSize = 20;
    for (let x = 0; x <= canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    
    for (let y = 0; y <= canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Draw current measurement line
    if (startPoint && endPoint) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(endPoint.x, endPoint.y);
      ctx.stroke();

      // Draw distance
      const distance = Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2)
      );
      const realDistance = rulerStore.convertPixelsToUnit(distance, rulerStore.currentUnit);
      
      const midX = (startPoint.x + endPoint.x) / 2;
      const midY = (startPoint.y + endPoint.y) / 2;

      ctx.fillStyle = '#1e293b';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        rulerStore.formatMeasurement(realDistance, rulerStore.currentUnit),
        midX,
        midY - 10
      );
    }
  }, [startPoint, endPoint, rulerStore.currentUnit]);

  const screenInfo = rulerStore.getScreenInfo();

  return (
    <div className="ruler-ui">
      <div className="ruler-ui__header">
        <h1>Digital Ruler</h1>
        <p>Measure objects on your screen with pixel-perfect accuracy</p>
      </div>

      <div className="ruler-ui__tabs">
        <button
          onClick={() => setActiveTab('ruler')}
          className={`ruler-ui__tab ${activeTab === 'ruler' ? 'ruler-ui__tab--active' : ''}`}
        >
          📏 Ruler
        </button>
        <button
          onClick={() => setActiveTab('calibration')}
          className={`ruler-ui__tab ${activeTab === 'calibration' ? 'ruler-ui__tab--active' : ''}`}
        >
          ⚙️ Calibration
        </button>
        <button
          onClick={() => setActiveTab('measurements')}
          className={`ruler-ui__tab ${activeTab === 'measurements' ? 'ruler-ui__tab--active' : ''}`}
        >
          📊 History ({rulerStore.allMeasurements.length})
        </button>
      </div>

      {activeTab === 'ruler' && (
        <div className="ruler-ui__section">
          <div className="ruler-ui__controls">
            <div className="ruler-ui__status">
              {rulerStore.isCalibrationSet ? (
                <span className="ruler-ui__status--calibrated">
                  ✓ Calibrated with {rulerStore.calibrationMethodUsed}
                </span>
              ) : (
                <span className="ruler-ui__status--uncalibrated">
                  ⚠️ Not calibrated - measurements may be inaccurate
                </span>
              )}
            </div>

            <div className="ruler-ui__unit-selector">
              <label>Unit:</label>
              <select
                value={rulerStore.currentUnit}
                onChange={(e) => rulerStore.setSelectedUnit(e.target.value)}
              >
                {rulerStore.availableUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ruler-ui__canvas-container">
            <canvas
              ref={canvasRef}
              width={800}
              height={400}
              className="ruler-ui__canvas"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            />
            <div className="ruler-ui__instructions">
              Click and drag to measure. {isDrawing ? 'Release to save measurement.' : ''}
            </div>
          </div>

          {showNameInput && (
            <div className="ruler-ui__name-input">
              <h3>Save Measurement</h3>
              <input
                type="text"
                value={measurementName}
                onChange={(e) => setMeasurementName(e.target.value)}
                placeholder="Enter measurement name (optional)"
                autoFocus
              />
              <div className="ruler-ui__name-actions">
                <button onClick={saveMeasurement} className="ruler-ui__save-button">
                  Save
                </button>
                <button onClick={cancelMeasurement} className="ruler-ui__cancel-button">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="ruler-ui__screen-info">
            <h3>Screen Information</h3>
            <div className="ruler-ui__info-grid">
              <div className="ruler-ui__info-item">
                <span>Resolution:</span>
                <span>{screenInfo.width} × {screenInfo.height} px</span>
              </div>
              <div className="ruler-ui__info-item">
                <span>Estimated PPI:</span>
                <span>{screenInfo.estimatedPPI.toFixed(1)}</span>
              </div>
              <div className="ruler-ui__info-item">
                <span>Pixels per mm:</span>
                <span>{rulerStore.currentPixelsPerMM.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'calibration' && (
        <div className="ruler-ui__section">
          <CalibrationTab />
        </div>
      )}

      {activeTab === 'measurements' && (
        <div className="ruler-ui__section">
          <MeasurementsTab />
        </div>
      )}
    </div>
  );
});

const CalibrationTab: React.FC = observer(() => {
  const [selectedMethod, setSelectedMethod] = useState(rulerStore.availableCalibrationMethods[0]);
  const [measuredPixels, setMeasuredPixels] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');

  const handleCalibrate = () => {
    try {
      const pixels = parseFloat(measuredPixels);
      const customSize = selectedMethod.id === 'custom' ? {
        width: parseFloat(customWidth),
        height: parseFloat(customHeight)
      } : undefined;

      rulerStore.calibrate(selectedMethod, pixels, customSize);
      
      // Reset form
      setMeasuredPixels('');
      setCustomWidth('');
      setCustomHeight('');
      
      alert('Calibration successful!');
    } catch (error) {
      alert('Calibration failed: ' + (error as Error).message);
    }
  };

  return (
    <div className="calibration-tab">
      <h2>Screen Calibration</h2>
      <p>Calibrate your screen for accurate measurements using a physical object.</p>

      <div className="calibration-tab__methods">
        <h3>Choose Calibration Method</h3>
        {rulerStore.availableCalibrationMethods.map(method => (
          <label key={method.id} className="calibration-tab__method">
            <input
              type="radio"
              name="calibration-method"
              checked={selectedMethod.id === method.id}
              onChange={() => setSelectedMethod(method)}
            />
            <div>
              <strong>{method.name}</strong>
              <p>{method.description}</p>
              {method.id !== 'custom' && (
                <small>Size: {method.realWorldSize.width} × {method.realWorldSize.height} mm</small>
              )}
            </div>
          </label>
        ))}
      </div>

      {selectedMethod.id === 'custom' && (
        <div className="calibration-tab__custom">
          <h4>Custom Object Dimensions</h4>
          <div className="calibration-tab__custom-inputs">
            <div>
              <label>Width (mm):</label>
              <input
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="Enter width in mm"
              />
            </div>
            <div>
              <label>Height (mm):</label>
              <input
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                placeholder="Enter height in mm"
              />
            </div>
          </div>
        </div>
      )}

      <div className="calibration-tab__measurement">
        <h4>Measure on Screen</h4>
        <p>Place your {selectedMethod.name.toLowerCase()} on the screen and measure its width in pixels.</p>
        <div>
          <label>Measured width (pixels):</label>
          <input
            type="number"
            value={measuredPixels}
            onChange={(e) => setMeasuredPixels(e.target.value)}
            placeholder="Enter pixel measurement"
          />
        </div>
      </div>

      <div className="calibration-tab__actions">
        <button
          onClick={handleCalibrate}
          disabled={!measuredPixels || (selectedMethod.id === 'custom' && (!customWidth || !customHeight))}
          className="calibration-tab__calibrate-button"
        >
          Calibrate
        </button>
        
        {rulerStore.isCalibrationSet && (
          <button
            onClick={() => rulerStore.resetCalibration()}
            className="calibration-tab__reset-button"
          >
            Reset Calibration
          </button>
        )}
      </div>

      {rulerStore.isCalibrationSet && (
        <div className="calibration-tab__status">
          <h4>Current Calibration</h4>
          <p>Method: {rulerStore.calibrationMethodUsed}</p>
          <p>Pixels per mm: {rulerStore.currentPixelsPerMM.toFixed(2)}</p>
          <p>Estimated PPI: {rulerStore.currentPixelsPerInch.toFixed(1)}</p>
        </div>
      )}
    </div>
  );
});

const MeasurementsTab: React.FC = observer(() => {
  return (
    <div className="measurements-tab">
      <div className="measurements-tab__header">
        <h2>Measurement History</h2>
        {rulerStore.allMeasurements.length > 0 && (
          <button
            onClick={() => rulerStore.clearAllMeasurements()}
            className="measurements-tab__clear-button"
          >
            Clear All
          </button>
        )}
      </div>

      {rulerStore.allMeasurements.length === 0 ? (
        <div className="measurements-tab__empty">
          <p>No measurements yet. Use the ruler to create your first measurement!</p>
        </div>
      ) : (
        <div className="measurements-tab__list">
          {rulerStore.allMeasurements.map(measurement => (
            <div key={measurement.id} className="measurements-tab__item">
              <div className="measurements-tab__item-info">
                <h4>{measurement.name}</h4>
                <p>{rulerStore.formatMeasurement(measurement.realLength, measurement.unit)}</p>
                <small>{measurement.pixelLength.toFixed(0)} pixels • {new Date(measurement.timestamp).toLocaleString()}</small>
              </div>
              <button
                onClick={() => rulerStore.deleteMeasurement(measurement.id)}
                className="measurements-tab__delete-button"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
